export type 頁面分類 =
  | "首頁"
  | "分類頁"
  | "內容頁"
  | "服務頁"
  | "醫師頁"
  | "案例頁"
  | "品牌頁"
  | "服務群組頁"
  | "醫師群組頁"
  | "評價頁";
export type 處理優先度 = "優先處理" | "後續補強" | "目前穩定";
export type 問題層級 = "此頁問題" | "全站問題";
export type 問題分類 =
  | "回答能力"
  | "可引用結構"
  | "可信度"
  | "主題對準"
  | "追蹤營運"
  | "結果觀察";

export interface 問題卡片 {
  key: string;
  title: string;
  category: 問題分類;
  scope: 問題層級;
  priority: 處理優先度;
  problem: string;
  howToFix: string[];
}

function 正規化問題字串(issue: string): string {
  return issue.replace(/\s+/g, " ").trim();
}

export function 是全站問題(issue: string): boolean {
  return /menu heading|導覽 heading|導覽.*噪音|全站|模板|選單|共用區塊|footer|header/i.test(issue);
}

function 建立問題卡片(
  key: string,
  title: string,
  category: 問題分類,
  priority: 處理優先度,
  problem: string,
  howToFix: string[],
  scope: 問題層級 = "此頁問題"
): 問題卡片 {
  return {
    key,
    title,
    category,
    priority,
    scope,
    problem,
    howToFix,
  };
}

function 是首頁(pageClassification: 頁面分類): boolean {
  return pageClassification === "首頁";
}

function 是分類頁(pageClassification: 頁面分類): boolean {
  return pageClassification === "分類頁";
}

function 是內容頁(pageClassification: 頁面分類): boolean {
  return pageClassification === "內容頁";
}

function 是服務頁(pageClassification: 頁面分類): boolean {
  return pageClassification === "服務頁";
}

function 是醫師頁(pageClassification: 頁面分類): boolean {
  return pageClassification === "醫師頁";
}

function 是案例頁(pageClassification: 頁面分類): boolean {
  return pageClassification === "案例頁";
}

function 是品牌頁(pageClassification: 頁面分類): boolean {
  return pageClassification === "品牌頁";
}

function 是服務群組頁(pageClassification: 頁面分類): boolean {
  return pageClassification === "服務群組頁";
}

function 是醫師群組頁(pageClassification: 頁面分類): boolean {
  return pageClassification === "醫師群組頁";
}

function 是評價頁(pageClassification: 頁面分類): boolean {
  return pageClassification === "評價頁";
}

function 建立作者相關卡片(pageClassification: 頁面分類): 問題卡片 | null {
  if (
    是首頁(pageClassification) ||
    是分類頁(pageClassification) ||
    是品牌頁(pageClassification) ||
    是服務群組頁(pageClassification) ||
    是醫師群組頁(pageClassification) ||
    是評價頁(pageClassification)
  ) {
    return null;
  }

  if (是內容頁(pageClassification)) {
    return 建立問題卡片(
      "author",
      "作者資訊不清楚",
      "可信度",
      "優先處理",
      "這一頁沒有清楚標示作者，或頁面可見作者與系統作者對不起來，讀者不容易判斷內容由誰撰寫。",
      [
        "在文章標題下方固定顯示作者名稱，不要露出後台帳號名稱。",
        "前台作者名稱、系統作者設定、頁面資料作者名稱要統一成同一套。",
        "如果是品牌團隊撰寫，請固定使用同一個團隊名稱。",
      ]
    );
  }

  if (是服務頁(pageClassification)) {
    return 建立問題卡片(
      "service-source",
      "服務頁缺少專業來源說明",
      "可信度",
      "優先處理",
      "這一頁在介紹療程或服務，但沒有清楚說明由哪個醫師團隊或專業來源提供內容依據，可信度不足。",
      [
        "在服務頁前段補上主責醫師、醫療團隊或專業來源欄位。",
        "把服務內容與對應醫師頁、團隊頁或案例頁連起來，不要只留品牌描述。",
        "如果服務頁由內容團隊整理，請補一段明確的醫療把關說明。",
      ]
    );
  }

  if (是醫師頁(pageClassification)) {
    return 建立問題卡片(
      "doctor-identity",
      "醫師身份資訊不清楚",
      "可信度",
      "優先處理",
      "這一頁沒有把醫師姓名、職稱、專長或身分說明整理清楚，讀者不容易建立信任。",
      [
        "把醫師姓名、職稱、專長與主治項目整理成固定區塊。",
        "把重要經歷、專科資格與臨床重點放在頁面前段。",
        "避免只放照片或簡短文案，讓這頁能清楚代表這位醫師。",
      ]
    );
  }

  return 建立問題卡片(
    "case-source",
    "案例資訊來源不清楚",
    "可信度",
    "優先處理",
    "這一頁有案例內容，但沒有清楚說明由哪位醫師或哪項療程負責，案例可信度不足。",
    [
      "在案例前段直接標示主責醫師與療程名稱。",
      "補上案例背景、治療目標與改善重點，讓案例來源更清楚。",
      "讓案例頁可連回對應醫師頁與服務頁，建立完整脈絡。",
    ]
  );
}

function 建立專業背書卡片(pageClassification: 頁面分類): 問題卡片 | null {
  if (是首頁(pageClassification) || 是分類頁(pageClassification) || 是服務群組頁(pageClassification)) return null;

  if (是品牌頁(pageClassification)) {
    return 建立問題卡片(
      "brand-trust",
      "品牌頁缺少可信度訊號",
      "可信度",
      "後續補強",
      "這個品牌頁沒有把診所特色、設備優勢、媒體露出或團隊實力整理成清楚的可信度訊號。",
      [
        "在前段補上診所特色、設備、專業團隊或媒體露出的重點整理。",
        "把品牌頁連到核心服務、醫師頁與案例頁，建立完整信任脈絡。",
        "避免只有品牌敘事，缺少可驗證的具體內容。",
      ]
    );
  }

  if (是醫師群組頁(pageClassification)) {
    return 建立問題卡片(
      "doctor-group-trust",
      "醫師群組頁缺少團隊信任訊號",
      "可信度",
      "後續補強",
      "這個醫師群組頁沒有先說明團隊分工、主要專長或醫療能力，團隊可信度不夠集中。",
      [
        "在頁面前段補一段團隊摘要，說明主要分科與擅長項目。",
        "每位醫師卡片補上專長與代表療程，不要只列人名。",
        "讓團隊頁能清楚導流到各醫師頁與核心服務頁。",
      ]
    );
  }

  if (是評價頁(pageClassification)) {
    return 建立問題卡片(
      "testimonial-trust",
      "評價頁缺少評價脈絡說明",
      "可信度",
      "後續補強",
      "這個評價頁沒有先說明評價來源、對應醫師或療程主題，讀者不容易理解這些評價的參考價值。",
      [
        "在頁面前段說明這頁整理的是哪一類評價或見證。",
        "把評價與對應醫師、服務或案例連起來，不要只放標題列表。",
        "如果有重點評價，可先整理成可快速閱讀的摘要區塊。",
      ]
    );
  }

  if (是內容頁(pageClassification)) {
    return 建立問題卡片(
      "reviewer",
      "缺少專業背書",
      "可信度",
      "優先處理",
      "這一頁有醫療內容，但沒有清楚交代由誰審稿或提供專業把關，可信度不足。",
      [
        "在作者資訊旁新增固定欄位，直接標示審稿醫師姓名與職稱。",
        "如果有醫師介紹頁，請讓審稿醫師名稱可連回對應頁面。",
        "前台可見的審稿資訊要和頁面設定一致，不要只存在系統資料裡。",
      ]
    );
  }

  if (是服務頁(pageClassification)) {
    return 建立問題卡片(
      "service-credibility",
      "服務頁缺少專業背書",
      "可信度",
      "優先處理",
      "這一頁在介紹療程，但沒有足夠的醫療專業訊號支撐，讀者不容易相信內容可信。",
      [
        "補上主責醫師、團隊或臨床經驗說明，讓服務頁不只是在介紹項目。",
        "把這項服務與案例、醫師專長或設備優勢連在一起。",
        "如果頁面涉及療程差異、風險或適用對象，請以醫療專業角度明確說明。",
      ]
    );
  }

  if (是醫師頁(pageClassification)) {
    return 建立問題卡片(
      "doctor-credibility",
      "醫師經歷與專長資訊不足",
      "可信度",
      "優先處理",
      "這一頁沒有把醫師的專科資格、臨床經歷或擅長項目講清楚，醫師頁的信任感不足。",
      [
        "補上醫師學經歷、專科資格與擅長療程。",
        "把最能建立信任的重點放在頁面前段，例如資歷、專長與代表案例。",
        "如果醫師有對應的核心服務，直接連到相關服務頁與案例頁。",
      ]
    );
  }

  return 建立問題卡片(
    "case-credibility",
    "案例缺少醫師或療程對應",
    "可信度",
    "優先處理",
    "這一頁案例缺少清楚的醫師或療程對應，讀者不容易判斷案例的真實性與參考價值。",
    [
      "在案例前段直接標示主責醫師、療程名稱與改善目標。",
      "補上案例前後的說明，避免只有圖片或簡短描述。",
      "讓案例頁連到對應服務頁與醫師頁，建立完整關聯。",
    ]
  );
}

function 建立摘要相關卡片(pageClassification: 頁面分類): 問題卡片 {
  if (是首頁(pageClassification)) {
    return 建立問題卡片(
      "home-summary",
      "首頁重點不夠清楚",
      "回答能力",
      "優先處理",
      "首頁前段沒有先講清楚診所定位、核心服務與主要優勢，讀者不容易快速理解這個網站在主打什麼。",
      [
        "在首頁第一屏補一段簡短摘要，直接說明診所定位、服務重點與主要差異。",
        "把核心服務放在首頁前段，不要讓讀者先看到過多次要資訊。",
        "首頁第一段先講結論，再補品牌背景或延伸介紹。",
      ]
    );
  }

  if (是分類頁(pageClassification)) {
    return 建立問題卡片(
      "archive-summary",
      "分類頁重點不夠清楚",
      "回答能力",
      "優先處理",
      "這個分類頁沒有先說明頁面主題與內容範圍，讀者不容易快速理解這一區在整理什麼資訊。",
      [
        "在分類頁最上方補一段短摘要，說明這個頁面整理的是哪一類主題。",
        "先交代這一頁適合誰看，再列出主要內容方向。",
        "把與此分類無關的品牌介紹或延伸內容往後移。",
      ]
    );
  }

  if (是品牌頁(pageClassification)) {
    return 建立問題卡片(
      "brand-guidance",
      "品牌頁缺少清楚導覽區塊",
      "回答能力",
      "後續補強",
      "這個品牌頁沒有把核心服務、醫師團隊、案例或交通資訊整理成清楚入口，讀者不容易快速找到下一步。",
      [
        "把核心服務、醫師團隊、案例與交通資訊整理成明確區塊。",
        "在前段直接放上最常用的入口，不要只靠選單導覽。",
        "用卡片或條列整理品牌頁的重要導流方向。",
      ]
    );
  }

  if (是服務群組頁(pageClassification)) {
    return 建立問題卡片(
      "service-group-guidance",
      "服務群組頁缺少清楚導覽區塊",
      "回答能力",
      "後續補強",
      "這個服務群組頁沒有把不同服務之間的差異與入口整理清楚，讀者不容易快速選到合適項目。",
      [
        "把服務依需求、治療目的或族群做清楚分組。",
        "每組服務前先補一句說明，幫助讀者理解差異。",
        "讓每個服務區塊都有明確的導流按鈕或連結。",
      ]
    );
  }

  if (是醫師群組頁(pageClassification)) {
    return 建立問題卡片(
      "doctor-group-guidance",
      "醫師群組頁缺少清楚導覽區塊",
      "回答能力",
      "後續補強",
      "這個醫師群組頁沒有把不同醫師的專長與分工整理清楚，讀者不容易快速選到對的人。",
      [
        "依專長、療程或部門重新分組醫師。",
        "每位醫師卡片都補上專長與對應服務。",
        "讓醫師群組頁能快速導流到個人頁與相關服務頁。",
      ]
    );
  }

  if (是評價頁(pageClassification)) {
    return 建立問題卡片(
      "testimonial-guidance",
      "評價頁缺少清楚導覽區塊",
      "回答能力",
      "後續補強",
      "這個評價頁沒有把評價主題、對應醫師或療程入口整理清楚，讀者不容易延伸閱讀。",
      [
        "把評價依主題、醫師或療程分組。",
        "在每組評價下方補上對應服務頁或醫師頁連結。",
        "讓評價頁不只是列表，而是可引導下一步的內容頁。",
      ]
    );
  }

  if (是品牌頁(pageClassification)) {
    return 建立問題卡片(
      "brand-summary",
      "品牌頁重點不夠清楚",
      "回答能力",
      "優先處理",
      "這個品牌頁前段沒有先講清楚診所定位、特色與主要優勢，讀者不容易快速理解這一頁的核心價值。",
      [
        "在第一屏先講清楚品牌定位、主要特色與適合對象。",
        "先讓讀者知道這頁要回答什麼，再補品牌背景。",
        "把品牌介紹濃縮成兩到四句可快速理解的摘要。",
      ]
    );
  }

  if (是服務群組頁(pageClassification)) {
    return 建立問題卡片(
      "service-group-summary",
      "服務群組頁重點不夠清楚",
      "回答能力",
      "優先處理",
      "這個服務群組頁沒有先整理這一區在介紹哪些服務，讀者不容易快速找到適合自己的療程方向。",
      [
        "在頁面前段補一段摘要，說明這組服務的主要範圍與適合對象。",
        "把服務分群整理清楚，避免只是一串名稱。",
        "先讓讀者知道如何選，再導去各服務頁。",
      ]
    );
  }

  if (是醫師群組頁(pageClassification)) {
    return 建立問題卡片(
      "doctor-group-summary",
      "醫師群組頁重點不夠清楚",
      "回答能力",
      "優先處理",
      "這個醫師群組頁沒有先說明團隊分工與專長範圍，讀者不容易快速找到適合的醫師。",
      [
        "在頁面前段補一段團隊摘要，說明主要分科與擅長項目。",
        "把醫師依專長或治療領域分組，而不是只列出人名。",
        "讓使用者能快速從團隊頁找到對應醫師頁。",
      ]
    );
  }

  if (是評價頁(pageClassification)) {
    return 建立問題卡片(
      "testimonial-summary",
      "評價頁重點不夠清楚",
      "回答能力",
      "優先處理",
      "這個評價頁沒有先講清楚這一頁整理的是哪類見證或評價，讀者不容易理解這頁的參考價值。",
      [
        "在頁面前段補一段摘要，說明評價主題、對應療程或醫師。",
        "把重點評價先整理成短摘要，再往下展開完整內容。",
        "讓讀者知道這些評價對應哪些實際服務或案例。",
      ]
    );
  }

  if (是服務頁(pageClassification)) {
    return 建立問題卡片(
      "service-summary",
      "服務頁重點不夠清楚",
      "回答能力",
      "優先處理",
      "這個服務頁一開始沒有先說清楚這項療程在解決什麼問題、適合誰，以及它和其他選項差在哪裡。",
      [
        "在服務頁前段先講這項服務適合誰、能解決什麼問題與主要特色。",
        "第一段不要先講品牌故事，先講療程價值與使用情境。",
        "把最重要的療程差異與核心賣點放在首屏可見位置。",
      ]
    );
  }

  if (是醫師頁(pageClassification)) {
    return 建立問題卡片(
      "doctor-summary",
      "醫師頁重點不夠清楚",
      "回答能力",
      "優先處理",
      "這一頁沒有先說清楚這位醫師的專長與定位，讀者不容易快速理解這位醫師適合處理哪些問題。",
      [
        "在醫師頁前段直接講清楚專長、擅長療程與適合處理的情況。",
        "把醫師的核心價值放在首屏，不要只堆疊一般介紹文字。",
        "首頁或服務頁若會導流到醫師頁，首段要能承接那個期待。",
      ]
    );
  }

  if (是案例頁(pageClassification)) {
    return 建立問題卡片(
      "case-summary",
      "案例情境不夠清楚",
      "回答能力",
      "優先處理",
      "這個案例頁沒有先交代案例背景、主要問題與改善方向，讀者不容易快速理解這個案例在說明什麼。",
      [
        "在案例前段先交代患者狀況、主要問題與治療目標。",
        "用兩到三句摘要直接說明案例重點，不要先鋪陳大量背景。",
        "把最重要的前後差異與改善結果提早呈現。",
      ]
    );
  }

  return 建立問題卡片(
    "summary",
    "開頭沒有先講答案",
    "回答能力",
    "優先處理",
    "這一頁前段沒有直接回答主問題，讀者與 AI 都要花比較長時間才能抓到重點。",
    [
      "在標題下方補一段兩到四句的摘要，直接回答主問題。",
      "第一段先講結論，再補背景與細節。",
      "把與主題無關的鋪陳往後移，不要放在最前面。",
    ]
  );
}

function 建立決策資訊卡片(pageClassification: 頁面分類): 問題卡片 {
  if (是首頁(pageClassification)) {
    return 建立問題卡片(
      "home-guidance",
      "首頁缺少清楚導覽",
      "回答能力",
      "優先處理",
      "首頁沒有把核心服務、適合對象或下一步行動講清楚，讀者不容易快速決定要點哪個內容或服務。",
      [
        "補一個首頁重點區塊，清楚列出核心服務與主要適合對象。",
        "把常見入口做成明確按鈕或導覽卡片，讓讀者知道下一步可以去哪裡。",
        "避免把太多不同主題塞在首頁同一區塊，先把路徑整理清楚。",
      ]
    );
  }

  if (是分類頁(pageClassification)) {
    return 建立問題卡片(
      "archive-guidance",
      "分類頁缺少清楚導覽",
      "回答能力",
      "優先處理",
      "這個分類頁沒有把主要內容方向與閱讀路徑講清楚，讀者不容易快速找到想看的子主題。",
      [
        "在分類頁前段加入重點導覽，說明這一類內容包含哪些主題。",
        "用清單或卡片把主要子題目排清楚，不要只丟文章列表。",
        "如果有高價值主題，放在分類頁前段優先導流。",
      ]
    );
  }

  if (是品牌頁(pageClassification)) {
    return 建立問題卡片(
      "brand-guidance",
      "品牌頁缺少清楚導覽區塊",
      "回答能力",
      "後續補強",
      "這個品牌頁沒有把核心服務、醫師團隊、案例或交通資訊整理成清楚入口，讀者不容易快速找到下一步。",
      [
        "把核心服務、醫師團隊、案例與交通資訊整理成明確區塊。",
        "在前段直接放上最常用的入口，不要只靠選單導覽。",
        "用卡片或條列整理品牌頁的重要導流方向。",
      ]
    );
  }

  if (是服務群組頁(pageClassification)) {
    return 建立問題卡片(
      "service-group-guidance",
      "服務群組頁缺少清楚導覽區塊",
      "回答能力",
      "後續補強",
      "這個服務群組頁沒有把不同服務之間的差異與入口整理清楚，讀者不容易快速選到合適項目。",
      [
        "把服務依需求、治療目的或族群做清楚分組。",
        "每組服務前先補一句說明，幫助讀者理解差異。",
        "讓每個服務區塊都有明確的導流按鈕或連結。",
      ]
    );
  }

  if (是醫師群組頁(pageClassification)) {
    return 建立問題卡片(
      "doctor-group-guidance",
      "醫師群組頁缺少清楚導覽區塊",
      "回答能力",
      "後續補強",
      "這個醫師群組頁沒有把不同醫師的專長與分工整理清楚，讀者不容易快速選到對的人。",
      [
        "依專長、療程或部門重新分組醫師。",
        "每位醫師卡片都補上專長與對應服務。",
        "讓醫師群組頁能快速導流到個人頁與相關服務頁。",
      ]
    );
  }

  if (是評價頁(pageClassification)) {
    return 建立問題卡片(
      "testimonial-guidance",
      "評價頁缺少清楚導覽區塊",
      "回答能力",
      "後續補強",
      "這個評價頁沒有把評價主題、對應醫師或療程入口整理清楚，讀者不容易延伸閱讀。",
      [
        "把評價依主題、醫師或療程分組。",
        "在每組評價下方補上對應服務頁或醫師頁連結。",
        "讓評價頁不只是列表，而是可引導下一步的內容頁。",
      ]
    );
  }

  if (是服務頁(pageClassification)) {
    return 建立問題卡片(
      "service-decision-info",
      "服務頁缺少重點資訊",
      "回答能力",
      "優先處理",
      "這個服務頁沒有把讀者最在意的適合對象、流程、差異、費用或限制說清楚，難以成為真正能協助決策的頁面。",
      [
        "補上適合對象、流程、費用區間與常見差異整理。",
        "把這項服務和其他常見方案的差別用條列或表格講清楚。",
        "在關鍵區塊補上下一步行動，例如諮詢、評估或對應案例入口。",
      ]
    );
  }

  if (是醫師頁(pageClassification)) {
    return 建立問題卡片(
      "doctor-guidance",
      "醫師頁缺少專長與服務對應",
      "回答能力",
      "優先處理",
      "這個醫師頁沒有把專長、擅長療程與適合處理的問題對應清楚，讀者不容易知道該不該找這位醫師。",
      [
        "把醫師專長整理成明確清單，對應常見治療需求。",
        "把醫師頁連到對應服務頁與案例頁，建立清楚路徑。",
        "如果這位醫師主打特定療程，前段就直接寫明，不要藏在後面。",
      ]
    );
  }

  if (是案例頁(pageClassification)) {
    return 建立問題卡片(
      "case-decision-info",
      "案例頁缺少療程重點",
      "回答能力",
      "優先處理",
      "這個案例頁沒有把療程內容、改善差異或適用情境整理清楚，案例的參考價值被削弱。",
      [
        "補上治療前狀況、採取的療程與改善結果。",
        "如果案例涉及多步驟療程，請用簡短流程整理。",
        "把案例最值得參考的差異點或選擇原因明確寫出來。",
      ]
    );
  }

  return 建立問題卡片(
    "decision-info",
    "缺少重點資訊",
    "回答能力",
    "優先處理",
    "這一頁沒有把讀者最在意的費用、流程、差異、適合對象或限制講清楚，不利於被當成答案頁。",
    [
      "補一段重點整理，直接說明費用、流程、適用對象或差異。",
      "把重要資訊整理成條列或表格，不要分散在長段落裡。",
      "如果是比較型內容，明確列出差異、優缺點與適用情境。",
    ]
  );
}

function 建立主題對準卡片(pageClassification: 頁面分類, urgent: boolean): 問題卡片 {
  const priority: 處理優先度 = urgent ? "優先處理" : "後續補強";

  if (是首頁(pageClassification)) {
    return 建立問題卡片(
      "home-focus",
      "首頁定位不夠集中",
      "主題對準",
      priority,
      "首頁目前同時承載太多訊息，核心服務與品牌定位不夠集中，系統不容易判斷首頁最重要的主題。",
      [
        "收斂首頁主軸，只保留診所定位、核心服務與主要差異。",
        "把次要活動、新聞或延伸資訊往後移，避免搶走首頁主題。",
        "讓標題、摘要與首屏重點都圍繞同一個首頁主軸。",
      ]
    );
  }

  if (是分類頁(pageClassification)) {
    return 建立問題卡片(
      "archive-focus",
      "分類頁主題不夠集中",
      "主題對準",
      priority,
      "這個分類頁目前沒有把主題範圍收斂好，讀者與系統都不容易判斷它在整理哪一類內容。",
      [
        "重新定義這個分類頁的主題範圍，避免混進不相干內容。",
        "分類頁標題、摘要與列表排序都要圍繞同一主題。",
        "把不同主題拆到其他分類頁，不要全部堆在同一頁。",
      ]
    );
  }

  if (是品牌頁(pageClassification)) {
    return 建立問題卡片(
      "brand-focus",
      "品牌頁定位不夠清楚",
      "主題對準",
      priority,
      "這個品牌頁同時承載太多不同訊息，讀者不容易理解這一頁最核心要傳達的品牌定位。",
      [
        "先決定這頁要主打品牌特色、環境優勢、技術定位或交通便利，避免全部一起講。",
        "把次要資訊往後移，讓前段只保留最核心的品牌訊息。",
        "標題與摘要都要對準同一個主題，不要分散。",
      ]
    );
  }

  if (是服務群組頁(pageClassification)) {
    return 建立問題卡片(
      "service-group-focus",
      "服務群組頁主題不夠集中",
      "主題對準",
      priority,
      "這個服務群組頁沒有明確界定整理範圍，頁面看起來像很多服務放在一起，但缺少清楚主軸。",
      [
        "先定義這組服務的主題，例如功能重建、前牙美學或牙周健康。",
        "把無關服務移出，避免群組範圍過大。",
        "在頁面標題與前段摘要直接說明這頁整理的是哪一類服務。",
      ]
    );
  }

  if (是醫師群組頁(pageClassification)) {
    return 建立問題卡片(
      "doctor-group-focus",
      "醫師群組頁主題不夠集中",
      "主題對準",
      priority,
      "這個醫師群組頁沒有清楚說明團隊分科邏輯，讀者不容易理解這一頁是依什麼方式在整理醫師。",
      [
        "依專長、部門或療程類別重新整理醫師群組。",
        "把群組邏輯寫在頁面前段，不要只靠排版暗示。",
        "如果同頁同時放太多不同型態醫師，請拆頁處理。",
      ]
    );
  }

  if (是評價頁(pageClassification)) {
    return 建立問題卡片(
      "testimonial-focus",
      "評價頁主題不夠集中",
      "主題對準",
      priority,
      "這個評價頁的整理方式不夠清楚，讀者不容易判斷這些評價是在談哪類主題或哪位醫師。",
      [
        "先決定這頁是主打醫師評價、療程見證，還是品牌口碑。",
        "不要把不同類型評價混在同一頁沒有分組。",
        "讓頁面標題、摘要與評價分組方式保持一致。",
      ]
    );
  }

  if (是服務頁(pageClassification)) {
    return 建立問題卡片(
      "service-focus",
      "服務頁主題不夠集中",
      "主題對準",
      priority,
      "這個服務頁目前混入太多延伸訊息，主要療程與核心差異不夠集中，不利於形成明確的引用主題。",
      [
        "把服務頁主軸收斂到單一療程，不要同頁同時承載太多不同治療。",
        "把標題、摘要、主要段落都圍繞這項服務展開。",
        "把不屬於這個療程的延伸內容拆到其他頁面或移到次要段落。",
      ]
    );
  }

  if (是醫師頁(pageClassification)) {
    return 建立問題卡片(
      "doctor-focus",
      "醫師頁定位不夠清楚",
      "主題對準",
      priority,
      "這個醫師頁沒有把專長與定位講清楚，讀者不容易理解這位醫師主要適合處理哪些需求。",
      [
        "讓頁面主標題與摘要直接講清楚醫師主打的專長方向。",
        "把次要經歷放到後段，前段優先說明臨床重點與主治項目。",
        "如果醫師跨多個療程，請先排序最核心的專長，不要平均分散。",
      ]
    );
  }

  if (是案例頁(pageClassification)) {
    return 建立問題卡片(
      "case-focus",
      "案例頁主題不夠集中",
      "主題對準",
      priority,
      "這個案例頁沒有把案例核心問題、療程方向與改善重點收斂好，讀者較難快速理解這個案例的代表性。",
      [
        "收斂案例主軸，先講最主要的治療問題與改善結果。",
        "不要在同一案例頁塞入太多不相干的療程背景或延伸衛教。",
        "讓標題、摘要與案例段落都圍繞同一個主題。",
      ]
    );
  }

  return 建立問題卡片(
    "topic-focus",
    "主題不夠集中",
    "主題對準",
    priority,
    "這一頁的主題訊號不夠集中，系統較難穩定判斷它要回答哪一個主要問題。",
    [
      "收斂這頁的主問題，只保留一個核心主題。",
      "讓標題、摘要、小標與主要段落都圍繞同一個問題展開。",
      "把不屬於主題的延伸內容拆成其他頁面或改成次要段落。",
    ]
  );
}

function 建立追蹤缺口卡片(pageClassification: 頁面分類): 問題卡片 {
  const problem =
    is頁型內容導向(pageClassification)
      ? "這一頁能帶出的核心字詞還沒有完整納入追蹤，後續很難觀察成果與放大成效。"
      : "這一頁對應的核心字詞還沒有完整納入追蹤，後續很難觀察這個頁型的主題表現與導流效果。";

  return 建立問題卡片(
    "tracking-gap",
    "關鍵字追蹤還沒補齊",
    "追蹤營運",
    "優先處理",
    problem,
    [
      "先把這頁最貼近主題的未追蹤關鍵字加入關鍵字列表。",
      "確認這些關鍵字有開啟 AI 監控，不只新增名稱而已。",
      "補完追蹤後，把此頁明確指定為對應的頁面，方便後續觀察。",
    ]
  );
}

function 建立長段落卡片(pageClassification: 頁面分類): 問題卡片 | null {
  if (
    是首頁(pageClassification) ||
    是分類頁(pageClassification) ||
    是醫師頁(pageClassification) ||
    是品牌頁(pageClassification) ||
    是服務群組頁(pageClassification) ||
    是醫師群組頁(pageClassification) ||
    是評價頁(pageClassification)
  ) {
    return null;
  }

  if (是服務頁(pageClassification)) {
    return 建立問題卡片(
      "service-long-paragraph",
      "服務頁內容太散，不好閱讀",
      "可引用結構",
      "後續補強",
      "這個服務頁把太多重點塞在長段落裡，讀者不容易快速掃描重點，也不利於取用重點資訊。",
      [
        "把服務頁長段拆成適合對象、流程、費用與差異等小區塊。",
        "每個區塊只講一個重點，避免把全部資訊堆成一段。",
        "重要資訊改成條列或重點框，提升掃描效率。",
      ]
    );
  }

  if (是案例頁(pageClassification)) {
    return 建立問題卡片(
      "case-long-paragraph",
      "案例說明太長，不好閱讀",
      "可引用結構",
      "後續補強",
      "這個案例頁的說明偏長，前後狀況與改善重點容易被埋住，讀者不容易快速抓到案例價值。",
      [
        "把案例背景、治療過程與改善結果拆成清楚段落。",
        "前後差異盡量用條列或對照方式呈現。",
        "把最重要的案例重點放在每段開頭，而不是放在段落最後。",
      ]
    );
  }

  return 建立問題卡片(
    "long-paragraph",
    "段落太長，不好閱讀",
    "可引用結構",
    "後續補強",
    "這一頁的長段落偏多，重點容易被埋住，讀者與 AI 都不容易快速抓到答案。",
    [
      "把長段拆成 2 到 3 段，每段只講一個重點。",
      "在長段前補上小標或重點句，讓掃描更容易。",
      "重要資訊盡量改成條列或表格，不要全部塞在一段文字裡。",
    ]
  );
}

function 建立更新資訊卡片(pageClassification: 頁面分類): 問題卡片 | null {
  if (
    是首頁(pageClassification) ||
    是分類頁(pageClassification) ||
    是品牌頁(pageClassification) ||
    是服務群組頁(pageClassification) ||
    是醫師群組頁(pageClassification) ||
    是評價頁(pageClassification)
  ) {
    return null;
  }

  if (是服務頁(pageClassification)) {
    return 建立問題卡片(
      "service-date",
      "服務頁缺少更新資訊",
      "可信度",
      "後續補強",
      "這個服務頁沒有清楚標示更新資訊，讀者不容易判斷內容是否仍然適用。",
      [
        "在頁面前段或文末補上最近更新日期。",
        "如果療程資訊會隨時間調整，請定期更新並同步調整日期。",
        "更新資訊要前台可見，不要只存在系統欄位裡。",
      ]
    );
  }

  if (是醫師頁(pageClassification)) {
    return 建立問題卡片(
      "doctor-date",
      "醫師頁缺少更新資訊",
      "可信度",
      "後續補強",
      "這個醫師頁沒有清楚標示更新資訊，讀者不容易判斷經歷與專長資訊是否仍然最新。",
      [
        "在醫師頁補上最近更新日期。",
        "醫師經歷、專長或職稱變動後，要同步更新頁面資訊。",
        "把更新日期放在容易看到的位置，不要只埋在系統資料中。",
      ]
    );
  }

  if (是案例頁(pageClassification)) {
    return 建立問題卡片(
      "case-date",
      "案例頁缺少更新資訊",
      "可信度",
      "後續補強",
      "這個案例頁沒有清楚標示更新資訊，讀者不容易判斷案例內容是否仍具參考價值。",
      [
        "補上案例頁更新日期。",
        "如果案例內容有補充說明或新圖片，更新後同步調整日期。",
        "讓日期顯示在案例說明附近，提升可信度。",
      ]
    );
  }

  return 建立問題卡片(
    "content-date",
    "缺少更新資訊",
    "可信度",
    "後續補強",
    "這一頁沒有清楚標示更新資訊，讀者不容易判斷內容是否仍然有效。",
    [
      "在文章前段或文末補上最近更新日期。",
      "內容有修訂時，同步更新前台日期顯示。",
      "更新日期要前台可見，不要只存在系統欄位裡。",
    ]
  );
}

function 建立問答卡片(pageClassification: 頁面分類): 問題卡片 | null {
  if (!是內容頁(pageClassification)) return null;
  return 建立問題卡片(
    "faq",
    "缺少問答區塊",
    "可引用結構",
    "後續補強",
    "這一頁缺少清楚的問答內容，讀者不容易快速找到常見問題的答案。",
    [
      "補上 3 到 5 個與此頁主題直接相關的常見問題。",
      "每個答案控制在短而明確的形式，不要只放空泛描述。",
      "問答內容必須前台可見，不要只存在系統設定裡。",
    ]
  );
}

function 建立結構卡片(pageClassification: 頁面分類): 問題卡片 {
  if (是首頁(pageClassification)) {
    return 建立問題卡片(
      "home-structure",
      "首頁缺少清楚區塊",
      "可引用結構",
      "後續補強",
      "首頁缺少清楚的服務區塊或導覽區塊，重點資訊沒有被整理成容易掃描的形式。",
      [
        "把首頁核心服務整理成獨立區塊或卡片，不要全部混在長段落裡。",
        "讓每個區塊只講一個主題，並搭配明確按鈕。",
        "重要內容可改成短清單，幫助讀者快速掃描。",
      ]
    );
  }

  if (是分類頁(pageClassification)) {
    return 建立問題卡片(
      "archive-structure",
      "分類頁缺少清楚導覽區塊",
      "可引用結構",
      "後續補強",
      "這個分類頁目前沒有把重點主題整理成清楚區塊，讀者不容易快速找到關心的內容。",
      [
        "用卡片、清單或區塊把主要子主題整理出來。",
        "讓高價值主題排在前面，不要只按照時間排序。",
        "每個區塊都加上一句簡短說明，幫助讀者判斷要點哪一個。",
      ]
    );
  }

  if (是服務頁(pageClassification)) {
    return 建立問題卡片(
      "service-structure",
      "服務頁缺少清楚比較或流程區塊",
      "可引用結構",
      "後續補強",
      "這個服務頁缺少條列、流程或比較區塊，重點資訊不夠集中，讀者難以快速理解療程差異。",
      [
        "涉及療程流程時，補一個步驟式區塊。",
        "涉及方案差異時，補一個比較表或差異整理區塊。",
        "把重要重點整理成條列或重點框，不要全部放在長文裡。",
      ]
    );
  }

  if (是醫師頁(pageClassification)) {
    return 建立問題卡片(
      "doctor-structure",
      "醫師頁缺少專長與案例導流區塊",
      "可引用結構",
      "後續補強",
      "這個醫師頁沒有把專長、對應服務與案例整理成清楚區塊，讀者不容易往下行動。",
      [
        "把醫師專長整理成清單或卡片，讓讀者快速理解重點。",
        "補上對應服務頁與代表案例的導流區塊。",
        "讓聯絡、預約或了解更多的下一步行動更清楚。",
      ]
    );
  }

  if (是案例頁(pageClassification)) {
    return 建立問題卡片(
      "case-structure",
      "案例頁缺少前後變化區塊",
      "可引用結構",
      "後續補強",
      "這個案例頁沒有把前後狀況、療程步驟與改善重點整理成清楚區塊，案例價值不夠集中。",
      [
        "用分段或對照方式整理案例前、中、後的狀況。",
        "把最值得參考的差異與治療重點做成清楚區塊。",
        "如果有圖片，請搭配文字說明，不要只放圖片。",
      ]
    );
  }

  return 建立問題卡片(
    "structure-blocks",
    "缺少容易被引用的內容結構",
    "可引用結構",
    "後續補強",
    "這一頁目前缺少條列、表格、比較或流程段，重點資訊不夠集中。",
    [
      "涉及比較時，補一個比較表或差異整理區塊。",
      "涉及流程時，補上步驟式內容。",
      "把重點改成條列或小表格，讓資訊更容易被取用。",
    ]
  );
}

function 建立標題卡片(pageClassification: 頁面分類): 問題卡片 {
  if (是首頁(pageClassification)) {
    return 建立問題卡片(
      "home-title-focus",
      "首頁訊息不夠聚焦",
      "主題對準",
      "後續補強",
      "首頁的標題與首段訊息不夠集中，讀者不容易一眼看懂這間診所最想強調的內容。",
      [
        "首頁主標題先講清楚診所定位與核心價值，不要混太多次要訊息。",
        "首段內容直接承接首頁主標題，避免跳到不相干主題。",
        "把首頁小標收斂到核心服務與差異化重點。",
      ]
    );
  }

  if (是分類頁(pageClassification)) {
    return 建立問題卡片(
      "archive-title-focus",
      "分類頁標題不夠清楚",
      "主題對準",
      "後續補強",
      "這個分類頁的標題與內容承接不夠清楚，讀者不容易立刻理解這頁整理的是哪一類資訊。",
      [
        "讓分類頁標題直接講清楚主題範圍。",
        "第一段承接標題，先說明這頁整理哪些內容。",
        "列表中的內容排序與標題方向要一致。",
      ]
    );
  }

  if (是品牌頁(pageClassification)) {
    return 建立問題卡片(
      "brand-title-focus",
      "品牌頁標題不夠清楚",
      "主題對準",
      "後續補強",
      "這個品牌頁的標題沒有直接說明頁面重點，讀者不容易從標題理解這頁主要價值。",
      [
        "讓標題直接對準這頁的主題，例如品牌特色、診所優勢、環境設備或交通便利。",
        "不要把太多不同概念塞進同一個標題。",
        "標題與頁面前段摘要要對準同一件事。",
      ]
    );
  }

  if (是服務群組頁(pageClassification) || 是醫師群組頁(pageClassification) || 是評價頁(pageClassification)) {
    const title = 是服務群組頁(pageClassification)
      ? "服務群組頁標題不夠清楚"
      : 是醫師群組頁(pageClassification)
        ? "醫師群組頁標題不夠清楚"
        : "評價頁標題不夠清楚";
    const problem = 是服務群組頁(pageClassification)
      ? "這個服務群組頁的標題沒有直接說明這一頁整理的是哪類服務。"
      : 是醫師群組頁(pageClassification)
        ? "這個醫師群組頁的標題沒有直接說明這一頁整理的是哪一類醫師或團隊。"
        : "這個評價頁的標題沒有直接說明這一頁整理的是哪類評價或見證。";
    return 建立問題卡片("group-title-focus", title, "主題對準", "後續補強", problem, [
      "讓標題直接對準頁面主題，不要只用模糊命名。",
      "標題與頁面前段摘要要說同一件事。",
      "如果這頁是整理頁，標題要能清楚表達整理範圍。",
    ]);
  }

  if (是服務頁(pageClassification)) {
    return 建立問題卡片(
      "service-title-focus",
      "服務頁標題不夠清楚",
      "主題對準",
      "後續補強",
      "這個服務頁的標題沒有明確點出療程主題、適用情境或主要差異，讀者不容易一眼理解頁面價值。",
      [
        "讓標題直接點出療程名稱與主要價值，不要同時塞太多概念。",
        "首段內容要直接承接標題，先講這項服務在解決什麼問題。",
        "如果頁面主打特定情境或族群，標題可直接帶出。",
      ]
    );
  }

  if (是醫師頁(pageClassification)) {
    return 建立問題卡片(
      "doctor-title-focus",
      "醫師頁標題不夠清楚",
      "主題對準",
      "後續補強",
      "這個醫師頁的標題沒有清楚帶出醫師身份與專長方向，頁面辨識度不足。",
      [
        "標題直接帶出醫師姓名、職稱與主打專長。",
        "首段內容承接標題，先講這位醫師擅長處理哪些需求。",
        "避免標題只寫品牌名稱或泛用介紹，讓頁面更有代表性。",
      ]
    );
  }

  if (是案例頁(pageClassification)) {
    return 建立問題卡片(
      "case-title-focus",
      "案例頁標題不夠清楚",
      "主題對準",
      "後續補強",
      "這個案例頁的標題沒有清楚帶出案例主題、療程方向或改善重點，辨識度不足。",
      [
        "標題直接說明案例主題與主要療程，不要只放模糊名稱。",
        "第一段承接標題，先講案例重點與改善方向。",
        "如果案例主打特定情境，標題可直接帶出問題與結果。",
      ]
    );
  }

  return 建立問題卡片(
    "title-focus",
    "標題不夠聚焦",
    "主題對準",
    "後續補強",
    "這一頁的標題與內容承接不夠集中，讀者不容易一眼看懂主題。",
    [
      "讓標題直接講清楚主題，不要堆太多概念。",
      "第一段要直接承接標題，不要跳題。",
      "小標也要圍繞同一個主題，避免一頁講太多不相干內容。",
    ]
  );
}

function is頁型內容導向(pageClassification: 頁面分類): boolean {
  return 是內容頁(pageClassification) || 是服務頁(pageClassification) || 是醫師頁(pageClassification) || 是案例頁(pageClassification);
}

export function 從問題字串建立卡片(issue: string, pageClassification: 頁面分類): 問題卡片 | null {
  const text = 正規化問題字串(issue);

  if (是全站問題(text)) return null;

  if (/未偵測到 canonical|沒有 JSON-LD/i.test(text)) {
    return null;
  }

  if (/主內容過短|資訊密度偏薄/i.test(text)) {
    return 建立摘要相關卡片(pageClassification);
  }

  if (
    /首頁重點不夠清楚|分類頁重點不夠清楚|品牌頁重點不夠清楚|服務群組頁重點不夠清楚|醫師群組頁重點不夠清楚|評價頁重點不夠清楚|服務頁重點不夠清楚|醫師頁重點不夠清楚|案例情境不夠清楚|開頭沒有先講答案/i.test(text)
  ) {
    return 建立摘要相關卡片(pageClassification);
  }

  if (
    /首頁缺少清楚導覽|分類頁缺少清楚導覽|品牌頁缺少清楚導覽區塊|服務群組頁缺少清楚導覽區塊|醫師群組頁缺少清楚導覽區塊|評價頁缺少清楚導覽區塊|服務頁缺少重點資訊|醫師頁缺少專長與服務對應|案例頁缺少療程重點|缺少重點資訊/i.test(text)
  ) {
    return 建立決策資訊卡片(pageClassification);
  }

  if (
    /首頁定位不夠集中|分類頁主題不夠集中|品牌頁定位不夠清楚|服務群組頁主題不夠集中|醫師群組頁主題不夠集中|評價頁主題不夠集中|服務頁主題不夠集中|醫師頁定位不夠清楚|案例頁主題不夠集中|主題不夠集中/i.test(
      text
    )
  ) {
    return 建立主題對準卡片(pageClassification, true);
  }

  if (
    /首頁缺少清楚區塊|分類頁缺少清楚導覽區塊|品牌頁缺少清楚導覽區塊|服務群組頁缺少清楚導覽區塊|醫師群組頁缺少清楚導覽區塊|評價頁缺少清楚導覽區塊|服務頁缺少清楚比較或流程區塊|醫師頁缺少專長與案例導流區塊|案例頁缺少前後變化區塊|缺少容易被引用的內容結構/i.test(text)
  ) {
    return 建立結構卡片(pageClassification);
  }

  if (
    /首頁訊息不夠聚焦|分類頁標題不夠清楚|品牌頁標題不夠清楚|服務群組頁標題不夠清楚|醫師群組頁標題不夠清楚|評價頁標題不夠清楚|服務頁標題不夠清楚|醫師頁標題不夠清楚|案例頁標題不夠清楚|標題不夠聚焦/i.test(text)
  ) {
    return 建立標題卡片(pageClassification);
  }

  if (/服務頁缺少更新資訊|醫師頁缺少更新資訊|案例頁缺少更新資訊|缺少更新資訊/i.test(text)) {
    return 建立更新資訊卡片(pageClassification);
  }

  if (/沒有清楚可見的作者|作者資訊|作者訊號出現多套版本|作者不一致/i.test(text)) {
    return 建立作者相關卡片(pageClassification);
  }

  if (/審稿|專業背書|醫療內容缺少/i.test(text)) {
    return 建立專業背書卡片(pageClassification);
  }

  if (/多個發布日期|多個更新日期|dateModified 不一致|FAQ schema|可見更新日期與 schema|不一致/i.test(text)) {
    return 建立問題卡片(
      "consistency",
      "頁面資料前後不一致",
      "可信度",
      "優先處理",
      "這一頁的日期、作者或問答資料彼此對不上，會讓內容可信度下降，也不利於後續營運判讀。",
      [
        "確認頁面前台顯示的發布日期與更新日期，只保留一個版本。",
        "如果有問答區塊，前台內容要和頁面資料完全一致。",
        "作者、日期與問答設定修正後，再重新檢查這一頁輸出是否一致。",
      ]
    );
  }

  if (/開頭沒有明確摘要|開頭沒有摘要句|開頭沒有直接回答|沒有摘要段/i.test(text)) {
    return 建立摘要相關卡片(pageClassification);
  }

  if (/決策資訊|可直接抽取的決策資訊詞|適用對象/i.test(text)) {
    return 建立決策資訊卡片(pageClassification);
  }

  if (/無法從文章主題穩定推導搜尋意圖|主題訊號仍可再聚焦|可服務意圖數量偏少/i.test(text)) {
    return 建立主題對準卡片(pageClassification, /無法從文章主題穩定推導搜尋意圖/i.test(text));
  }

  if (/尚未納入追蹤|未進入追蹤名單|沒有對應的已追蹤意圖|未開啟 AI Overview 監控/i.test(text)) {
    return 建立追蹤缺口卡片(pageClassification);
  }

  if (/沒有清楚的發布／更新日期/i.test(text)) {
    return 建立更新資訊卡片(pageClassification);
  }

  if (/過長段落|不易掃描/i.test(text)) {
    return 建立長段落卡片(pageClassification);
  }

  if (/FAQ|問答/i.test(text)) {
    return 建立問答卡片(pageClassification);
  }

  if (/小標不足|主內容層次不夠清楚/i.test(text)) {
    return 建立結構卡片(pageClassification);
  }

  if (/清單|表格|比較|流程/i.test(text)) {
    return 建立結構卡片(pageClassification);
  }

  if (/title|H1|標題/i.test(text)) {
    return 建立標題卡片(pageClassification);
  }

  return 建立問題卡片(
    `generic-${pageClassification}-${text}`,
    is頁型內容導向(pageClassification) ? "此頁還有待整理的內容問題" : "此頁還有待整理的頁面問題",
    "回答能力",
    "後續補強",
    text,
    [
      "先確認這是此頁內容本身的問題，而不是全站模板造成。",
      "如果是此頁問題，就直接在這頁補足資訊或調整段落安排。",
      "修正後再重新檢查這頁的潛在意圖與問題摘要是否有改善。",
    ]
  );
}

export function 建立問題卡片清單(input: {
  pageClassification: 頁面分類;
  issues: string[];
}): 問題卡片[] {
  const deduped = new Map<string, 問題卡片>();
  for (const rawIssue of input.issues) {
    const issue = 正規化問題字串(rawIssue);
    if (!issue) continue;
    const card = 從問題字串建立卡片(issue, input.pageClassification);
    if (!card) continue;
    if (!deduped.has(card.key)) deduped.set(card.key, card);
  }
  return Array.from(deduped.values());
}
